/**
 * ============================================================================
 * HOUSEHOLD CONTEXT v3.1 - UNIFIED AUTH SUPPORT
 * ============================================================================
 * 
 * 🔧 v3.1: UNIFIED AUTH - Verwendet useAuth statt useLogto für Native Support
 * 🔧 v3.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 *          - useToken() statt useAuth().getAccessToken()
 *          - Kein individueller Token-Fetch mehr
 *          - Verhindert Re-Render Loop
 * 
 * ✅ v2.2: CRITICAL FIX - Verwendet household_users.id (INTEGER)
 * ✅ v2.1: Infinite Loop Fix
 * ✅ v2.0: Planivio Rebranding
 * 
 * @version 3.1.0 - Unified Auth Support (Native + Web)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
// ✅ v3.1: useAuth statt useLogto für Native-Unterstützung
import { useAuth } from '../hooks/useAuth';
import { useToken } from './TokenContext';

// ============================================================================
// CONFIGURATION
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============================================================================
// GLOBALER STATE (außerhalb React)
// ============================================================================
let GLOBAL_MEMBERS = [];
let GLOBAL_HOUSEHOLD_INFO = null;
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;
let GLOBAL_LAST_FETCH = null;

// ============================================================================
// CONTEXT
// ============================================================================
const HouseholdContext = createContext(null);

// ============================================================================
// PROVIDER
// ============================================================================
export function HouseholdProvider({ children }) {
  // ✅ v3.1: useAuth statt useLogto
  const { isAuthenticated } = useAuth();
  const { token, loading: tokenLoading } = useToken();
  
  // Initialisiere aus Global Cache
  const [members, setMembers] = useState(() => GLOBAL_MEMBERS);
  const [householdInfo, setHouseholdInfo] = useState(() => GLOBAL_HOUSEHOLD_INFO);
  const [loading, setLoading] = useState(() => !GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync State zu Global Cache
  useEffect(() => {
    if (members.length > 0) {
      GLOBAL_MEMBERS = members;
    }
    if (householdInfo) {
      GLOBAL_HOUSEHOLD_INFO = householdInfo;
    }
  }, [members, householdInfo]);

  // ============================================================================
  // LOAD MEMBERS
  // ============================================================================
  const loadMembers = useCallback(async (force = false) => {
    if (!isAuthenticated) {
      if (isMountedRef.current) {
        setLoading(false);
      }
      return;
    }

    if (!token) {
      return;
    }

    // Bereits geladen und nicht forced?
    if (GLOBAL_HAS_LOADED && GLOBAL_MEMBERS.length > 0 && !force) {
      if (GLOBAL_LAST_FETCH && Date.now() - GLOBAL_LAST_FETCH < 5 * 60 * 1000) {
        if (isMountedRef.current) {
          setMembers(GLOBAL_MEMBERS);
          setHouseholdInfo(GLOBAL_HOUSEHOLD_INFO);
          setLoading(false);
        }
        return;
      }
    }

    // Bereits ein Load läuft?
    if (GLOBAL_LOADING_PROMISE) {
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        if (isMountedRef.current) {
          setMembers(result.members);
          setHouseholdInfo(result.householdInfo);
          setLoading(false);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err.message);
          setLoading(false);
        }
      }
      return;
    }

    if (isMountedRef.current) {
      setLoading(true);
      setError(null);
    }

    const fetchPromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/api/household/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.users) {
        return {
          members: data.users,
          householdInfo: data.household || null
        };
      } else {
        throw new Error(data.error || 'Failed to load household members');
      }
    })();

    GLOBAL_LOADING_PROMISE = fetchPromise;

    try {
      const result = await fetchPromise;
      
      GLOBAL_MEMBERS = result.members;
      GLOBAL_HOUSEHOLD_INFO = result.householdInfo;
      GLOBAL_HAS_LOADED = true;
      GLOBAL_LAST_FETCH = Date.now();
      
      if (isMountedRef.current) {
        setMembers(result.members);
        setHouseholdInfo(result.householdInfo);
      }
      
      return result;
      
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.message);
        if (GLOBAL_MEMBERS.length > 0) {
          setMembers(GLOBAL_MEMBERS);
          setHouseholdInfo(GLOBAL_HOUSEHOLD_INFO);
        } else {
          setMembers([]);
        }
      }
      
      throw err;
      
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, token]);

  // ============================================================================
  // INITIAL LOAD
  // ============================================================================
  const initialLoadDone = useRef(false);
  
  useEffect(() => {
    if (GLOBAL_HAS_LOADED && GLOBAL_MEMBERS.length > 0) {
      setMembers(GLOBAL_MEMBERS);
      setHouseholdInfo(GLOBAL_HOUSEHOLD_INFO);
      setLoading(false);
      return;
    }
    
    if (tokenLoading || !token) {
      return;
    }
    
    if (initialLoadDone.current) {
      return;
    }
    
    if (isAuthenticated) {
      initialLoadDone.current = true;
      loadMembers().catch(err => console.warn('HouseholdContext: loadMembers failed:', err.message));
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token, tokenLoading, loadMembers]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const getMemberOptions = useCallback((includeAll = true) => {
    const options = [];

    if (includeAll) {
      options.push({
        id: 'alle',
        value: 'alle',
        name: 'Alle',
        label: 'Alle',
        avatar: '👥',
        icon: '👥'
      });
    }

    members.forEach(member => {
      const displayName = member.display_name && member.display_name !== 'Mitglied'
        ? member.display_name
        : member.email?.split('@')[0] || `User-${member.user_id?.substring(0, 6)}`;

      let avatar = '👤';
      if (member.role === 'owner') avatar = '👑';
      else if (member.role === 'admin') avatar = '⭐';

      options.push({
        id: member.id,
        value: member.id,
        user_id: member.user_id,
        name: displayName,
        label: displayName,
        avatar: avatar,
        icon: avatar,
        role: member.role,
        email: member.email,
        isMe: member.is_me || false
      });
    });

    return options;
  }, [members]);

  const getMemberById = useCallback((idOrUserId) => {
    if (idOrUserId === 'alle') {
      return { id: 'alle', name: 'Alle', avatar: '👥' };
    }
    
    return members.find(m => 
      m.id === idOrUserId || 
      m.id === parseInt(idOrUserId) ||
      m.user_id === idOrUserId
    ) || null;
  }, [members]);

  const getMemberName = useCallback((idOrUserId) => {
    if (idOrUserId === 'alle' || idOrUserId === null || idOrUserId === undefined) {
      return 'Alle';
    }
    
    const member = members.find(m => 
      m.id === idOrUserId || 
      m.id === parseInt(idOrUserId) ||
      m.user_id === idOrUserId
    );
    
    if (!member) {
      if (typeof idOrUserId === 'number' || !isNaN(parseInt(idOrUserId))) {
        return `Mitglied #${idOrUserId}`;
      }
      return idOrUserId?.substring?.(0, 8) || 'Unbekannt';
    }
    
    return member.display_name && member.display_name !== 'Mitglied'
      ? member.display_name
      : member.email?.split('@')[0] || `User-${member.user_id?.substring(0, 6)}`;
  }, [members]);

  const getCurrentUser = useCallback(() => {
    return members.find(m => m.is_me) || null;
  }, [members]);

  const refreshMembers = useCallback(() => {
    return loadMembers(true);
  }, [loadMembers]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const contextValue = useMemo(() => ({
    members,
    householdInfo,
    loading,
    error,
    getMemberOptions,
    getMemberById,
    getMemberName,
    getCurrentUser,
    refreshMembers,
    loadMembers
  }), [
    members,
    householdInfo,
    loading,
    error,
    getMemberOptions,
    getMemberById,
    getMemberName,
    getCurrentUser,
    refreshMembers,
    loadMembers
  ]);

  return (
    <HouseholdContext.Provider value={contextValue}>
      {children}
    </HouseholdContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================
export function useHousehold() {
  const context = useContext(HouseholdContext);
  
  if (!context) {
    return {
      members: [],
      householdInfo: null,
      loading: false,
      error: 'HouseholdProvider not found',
      getMemberOptions: (includeAll = true) => includeAll 
        ? [{ id: 'alle', value: 'alle', name: 'Alle', label: 'Alle', avatar: '👥', icon: '👥' }] 
        : [],
      getMemberById: () => null,
      getMemberName: (id) => id || 'Unbekannt',
      getCurrentUser: () => null,
      refreshMembers: () => Promise.resolve(),
      loadMembers: () => Promise.resolve()
    };
  }
  
  return context;
}

export default HouseholdContext;
